import type { HsHypersceneTypes } from '@lib/core-ts/math-new/types/hyperscene-types.type';
import type { HsMeasurementMode, HsMeasurementUnit } from '@sibe/hyperscene/tools/measurement';
import type {
  HypersceneAttributeView,
  HypersceneIngestAdapterService,
  HypersceneNodeCreateInfo,
  HypersceneNodeSmallView,
  HypersceneNodeTransformView,
  HypersceneNodeView,
} from './hyperscene-ingest-adapter.service';

export interface HypersceneViewpoint {
  id: string;
  name: string;
  cameraMatrix: HsHypersceneTypes.Array16;
}

export interface HypersceneMarker {
  id: string;
  name: string;
  position: HsHypersceneTypes.Array3;
}

export interface HypersceneViewerTransferPayload {
  version: number;
  exportedAt: string;
  viewpoints: HypersceneViewpoint[];
  markers: HypersceneMarker[];
}

export interface HypersceneMeasurementEntry {
  id: string;
  pointA: HsHypersceneTypes.Array3;
  pointB: HsHypersceneTypes.Array3;
  distance: number;
  source: 'snap' | 'plane';
  createdAt: string;
  mode?: HsMeasurementMode;
  points?: HsHypersceneTypes.Array3[];
  unit?: HsMeasurementUnit;
  /** Real meters per world unit the value was calibrated with; 1 or absent when not calibrated. */
  scale?: number;
}

const VIEWPOINT_NODE_TAG: string = 'HypersceneDemo.Viewpoint';
const MARKER_NODE_TAG: string = 'HypersceneDemo.Marker';
const MARKERS_STORAGE_KEY: string = 'hyperscene-demo.markers.v1';
const MEASUREMENTS_STORAGE_KEY: string = 'hyperscene-demo.measurements.v1';
const CALIBRATIONS_STORAGE_KEY: string = 'hyperscene-demo.measurement-calibrations.v1';
const VIEWPOINT_MATRIX_ATTRIBUTE_GROUP: string = 'MainGroup';
const VIEWPOINT_MATRIX_ATTRIBUTE_NAME: string = 'CameraMatrix';

/**
 * Where the viewer page keeps its annotations: viewpoints and markers as tagged root nodes of the scene,
 * markers and measurements in the browser storage, and all of them in the JSON transfer format.
 */
export class ViewerAnnotationsStore {
  public constructor(
    private readonly ingestAdapter: HypersceneIngestAdapterService,
    private readonly apiBaseUrl: () => string
  ) {}

  public async loadViewpoints(sceneId: string): Promise<HypersceneViewpoint[]> {
    const baseUrl: string = this.apiBaseUrl();
    const nodes: HypersceneNodeSmallView[] = await this.ingestAdapter.getSceneRootNodes(baseUrl, sceneId, [
      VIEWPOINT_NODE_TAG,
    ]);

    const viewpoints: (HypersceneViewpoint | null)[] = await Promise.all(
      nodes.map(async (node: HypersceneNodeSmallView): Promise<HypersceneViewpoint | null> => {
        const fullNode: HypersceneNodeView = await this.ingestAdapter.getNode(baseUrl, node.id);
        // Viewpoints keep the full camera matrix in an attribute; older ones only have the node transform.
        let cameraMatrix: HsHypersceneTypes.Array16 | null = parseViewpointMatrix(fullNode.attributes);
        if (cameraMatrix === null) {
          const transform: HypersceneNodeTransformView = await this.ingestAdapter.getNodeTransform(baseUrl, node.id);
          cameraMatrix = toArray16OrNull(transform.worldMatrix);
        }
        if (cameraMatrix === null) {
          return null;
        }

        const externalIdentifier: string = fullNode.externalId?.externalIdentifier?.trim() ?? '';
        return {
          id: externalIdentifier.length > 0 ? externalIdentifier : fullNode.id,
          name: fullNode.title,
          cameraMatrix,
        };
      })
    );

    return viewpoints.filter(
      (viewpoint: HypersceneViewpoint | null): viewpoint is HypersceneViewpoint => viewpoint !== null
    );
  }

  public async loadMarkers(sceneId: string): Promise<HypersceneMarker[]> {
    const baseUrl: string = this.apiBaseUrl();
    const nodes: HypersceneNodeSmallView[] = await this.ingestAdapter.getSceneRootNodes(baseUrl, sceneId, [
      MARKER_NODE_TAG,
    ]);

    const markers: (HypersceneMarker | null)[] = await Promise.all(
      nodes.map(async (node: HypersceneNodeSmallView): Promise<HypersceneMarker | null> => {
        const transform: HypersceneNodeTransformView = await this.ingestAdapter.getNodeTransform(baseUrl, node.id);
        const position: HsHypersceneTypes.Array3 | null = extractPositionFromMatrix(transform.worldMatrix);
        return position === null ? null : { id: node.id, name: node.title, position };
      })
    );

    return markers.filter((item: HypersceneMarker | null): item is HypersceneMarker => item !== null);
  }

  public async saveViewpoints(sceneId: string, viewpoints: HypersceneViewpoint[]): Promise<void> {
    await this.replaceTaggedSceneNodes(sceneId, VIEWPOINT_NODE_TAG, viewpoints.map(makeViewpointNodeCreateInfo));
  }

  public async saveMarkers(sceneId: string, markers: HypersceneMarker[]): Promise<void> {
    await this.replaceTaggedSceneNodes(sceneId, MARKER_NODE_TAG, markers.map(makeMarkerNodeCreateInfo));
  }

  /** Stored markers, or null when the storage has none or cannot be read. */
  public readLocalMarkers(): HypersceneMarker[] | null {
    return readStoredList(MARKERS_STORAGE_KEY, isValidMarker);
  }

  /** Stored measurements, or null when the storage has none or cannot be read. */
  public readLocalMeasurements(): HypersceneMeasurementEntry[] | null {
    return readStoredList(MEASUREMENTS_STORAGE_KEY, isValidMeasurement);
  }

  public storeLocalMarkers(markers: HypersceneMarker[]): void {
    getBrowserStorage()?.setItem(MARKERS_STORAGE_KEY, JSON.stringify(markers));
  }

  public storeLocalMeasurements(measurements: HypersceneMeasurementEntry[]): void {
    getBrowserStorage()?.setItem(MEASUREMENTS_STORAGE_KEY, JSON.stringify(measurements));
  }

  /** Real meters per world unit of a scene's models, as calibrated from a known length; 1 when not calibrated. */
  public readMeasurementScale(sceneId: string): number {
    const scale: unknown = readCalibrations()[sceneId];
    return typeof scale === 'number' && Number.isFinite(scale) && scale > 0 ? scale : 1;
  }

  /** Stores a scene's calibration; a scale of 1 removes it. */
  public storeMeasurementScale(sceneId: string, scale: number): void {
    const calibrations: Record<string, number> = Object.fromEntries(
      Object.entries(readCalibrations()).filter(([id]: [string, number]) => id !== sceneId)
    );
    if (scale !== 1) {
      calibrations[sceneId] = scale;
    }
    getBrowserStorage()?.setItem(CALIBRATIONS_STORAGE_KEY, JSON.stringify(calibrations));
  }

  public makeTransferPayload(
    viewpoints: HypersceneViewpoint[],
    markers: HypersceneMarker[]
  ): HypersceneViewerTransferPayload {
    return { version: 1, exportedAt: new Date().toISOString(), viewpoints, markers };
  }

  /** The payload of transfer JSON with its invalid entries dropped, or null when it is no payload at all. */
  public parseTransferPayload(value: unknown): HypersceneViewerTransferPayload | null {
    if (typeof value !== 'object' || value === null) {
      return null;
    }
    const payload: Partial<HypersceneViewerTransferPayload> = value;
    if (typeof payload.version !== 'number' || !Array.isArray(payload.viewpoints) || !Array.isArray(payload.markers)) {
      return null;
    }
    return {
      version: payload.version,
      exportedAt: typeof payload.exportedAt === 'string' ? payload.exportedAt : '',
      viewpoints: payload.viewpoints.filter(isValidViewpoint),
      markers: payload.markers.filter(isValidMarker),
    };
  }

  private async replaceTaggedSceneNodes(
    sceneId: string,
    tag: string,
    nodeInfos: HypersceneNodeCreateInfo[]
  ): Promise<void> {
    const baseUrl: string = this.apiBaseUrl();
    const existingNodes: HypersceneNodeSmallView[] = await this.ingestAdapter.getSceneRootNodes(baseUrl, sceneId, [
      tag,
    ]);

    for (const existingNode of existingNodes) {
      await this.ingestAdapter.deleteNode(baseUrl, existingNode.id);
    }

    for (const nodeInfo of nodeInfos) {
      const createdNode: HypersceneNodeView = await this.ingestAdapter.createNode(baseUrl, nodeInfo);
      await this.ingestAdapter.putNodeTreeToScene(baseUrl, sceneId, createdNode.id);
    }
  }
}

/** Whether a scene root node holds an annotation rather than scene content. */
export function isAnnotationNode(node: HypersceneNodeSmallView): boolean {
  return node.tags.includes(VIEWPOINT_NODE_TAG) || node.tags.includes(MARKER_NODE_TAG);
}

/** Items of both lists by id; imported items replace current ones with the same id. */
export function mergeById<T extends { id: string }>(currentItems: T[], importedItems: T[]): T[] {
  const merged: Map<string, T> = new Map<string, T>();
  for (const item of [...currentItems, ...importedItems]) {
    merged.set(item.id, item);
  }
  return Array.from(merged.values());
}

function getBrowserStorage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function readCalibrations(): Record<string, number> {
  try {
    const parsed: unknown = JSON.parse(getBrowserStorage()?.getItem(CALIBRATIONS_STORAGE_KEY) ?? '{}');
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, number>)
      : {};
  } catch {
    return {};
  }
}

function readStoredList<T>(key: string, isValid: (value: unknown) => value is T): T[] | null {
  try {
    const raw: string | null = getBrowserStorage()?.getItem(key) ?? null;
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isValid) : null;
  } catch {
    return null;
  }
}

function isValidViewpoint(value: unknown): value is HypersceneViewpoint {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate: Partial<HypersceneViewpoint> = value;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.name === 'string' &&
    Array.isArray(candidate.cameraMatrix) &&
    candidate.cameraMatrix.length === 16
  );
}

function isValidMarker(value: unknown): value is HypersceneMarker {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate: Partial<HypersceneMarker> = value;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.name === 'string' &&
    Array.isArray(candidate.position) &&
    candidate.position.length === 3
  );
}

function isValidMeasurement(value: unknown): value is HypersceneMeasurementEntry {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate: Partial<HypersceneMeasurementEntry> = value;
  const hasLegacyPoints: boolean =
    Array.isArray(candidate.pointA) &&
    candidate.pointA.length === 3 &&
    Array.isArray(candidate.pointB) &&
    candidate.pointB.length === 3;
  const hasPointsArray: boolean =
    Array.isArray(candidate.points) &&
    candidate.points.length >= 1 &&
    candidate.points.every(
      (point: unknown) =>
        Array.isArray(point) && point.length === 3 && point.every((coord: unknown) => typeof coord === 'number')
    );

  return (
    typeof candidate.id === 'string' &&
    (hasLegacyPoints || hasPointsArray) &&
    typeof candidate.distance === 'number' &&
    (candidate.source === 'snap' || candidate.source === 'plane') &&
    typeof candidate.createdAt === 'string'
  );
}

function makeViewpointNodeCreateInfo(viewpoint: HypersceneViewpoint): HypersceneNodeCreateInfo {
  const position: HsHypersceneTypes.Array3 = extractPositionFromMatrix(viewpoint.cameraMatrix) ?? [0, 0, 0];
  return {
    parentId: null,
    externalName: viewpoint.name,
    attributes: [
      {
        name: VIEWPOINT_MATRIX_ATTRIBUTE_NAME,
        value: JSON.stringify(viewpoint.cameraMatrix),
        groupName: VIEWPOINT_MATRIX_ATTRIBUTE_GROUP,
        unit: '',
      },
    ],
    externalIdentifier: viewpoint.id,
    transformInfo: makeTransformInfo(position),
    tags: [VIEWPOINT_NODE_TAG],
  };
}

function makeMarkerNodeCreateInfo(marker: HypersceneMarker): HypersceneNodeCreateInfo {
  return {
    parentId: null,
    externalName: marker.name,
    attributes: [],
    externalIdentifier: marker.id,
    transformInfo: makeTransformInfo(marker.position),
    tags: [MARKER_NODE_TAG],
  };
}

function makeTransformInfo(position: HsHypersceneTypes.Array3): HypersceneNodeCreateInfo['transformInfo'] {
  return {
    translation: { x: position[0], y: position[1], z: position[2] },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
  };
}

function parseViewpointMatrix(attributes: HypersceneAttributeView[]): HsHypersceneTypes.Array16 | null {
  const matrixAttribute: HypersceneAttributeView | undefined = attributes.find(
    (attribute: HypersceneAttributeView) =>
      attribute.groupName === VIEWPOINT_MATRIX_ATTRIBUTE_GROUP && attribute.name === VIEWPOINT_MATRIX_ATTRIBUTE_NAME
  );
  if (matrixAttribute === undefined) {
    return null;
  }

  try {
    const value: unknown = JSON.parse(matrixAttribute.value);
    return Array.isArray(value) ? toArray16OrNull(value.map((item: unknown) => Number(item))) : null;
  } catch {
    return null;
  }
}

function toArray16OrNull(value: number[]): HsHypersceneTypes.Array16 | null {
  if (value.length !== 16 || value.some((item: number) => !Number.isFinite(item))) {
    return null;
  }

  return [...value] as HsHypersceneTypes.Array16;
}

/** The translation of a column-major matrix, or of a row-major one when the former is not finite. */
function extractPositionFromMatrix(matrix: number[]): HsHypersceneTypes.Array3 | null {
  const candidates: HsHypersceneTypes.Array3[] = [];

  if (matrix.length >= 15) {
    candidates.push([matrix[12], matrix[13], matrix[14]]);
  }

  if (matrix.length >= 12) {
    candidates.push([matrix[3], matrix[7], matrix[11]]);
  }

  return candidates.find((candidate: HsHypersceneTypes.Array3) => candidate.every(Number.isFinite)) ?? null;
}
